import { useMemo } from 'react';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useSupporterVideos } from '#src/app/pages/petitionDetails/shared/hooks';

import type { SupporterVideo } from '../../../types';
import { useSupporterComments } from '../../SupporterVoicesComments/hooks/useSupporterComments';
import type { SupporterComment, SupporterCommentPetition } from '../../SupporterVoicesComments/shared/types';

// When the FCM is on, show the mixed feed for any petition with at least one supporter voice
// (comment or video) — including comments-only petitions. Only fall back to the legacy layout
// when there's nothing to render.
const MIN_ITEMS_TO_SHOW = 1;
const DEFAULT_ITEMS_PER_ROW = 3;
const DEFAULT_MAX_ROWS = 2;
const DEFAULT_COMMENTS_PER_VIDEO = 1;

export type FeedItem = { type: 'comment'; data: SupporterComment } | { type: 'video'; data: SupporterVideo };

type Result = ModelHookResult<
	{
		feedItems: readonly FeedItem[];
		petition: SupporterCommentPetition;
		currentlyPlaying: string;
		showFeed: boolean;
		showCreateVideo: boolean;
		itemsPerRow: number;
	},
	{
		setCurrentlyPlaying: (videoUrl: string) => void;
		removeVideoFromState: (videoId?: string) => void;
	}
>;

export function useSupporterVoicesFeed(): Result {
	const {
		data: {
			supporterVoicesVideos,
			featureConfigs: { supporterVoicesMix },
		},
	} = usePetitionDetailsPageData();

	const itemsPerRow = supporterVoicesMix?.itemsPerRow ?? DEFAULT_ITEMS_PER_ROW;
	const maxRows = supporterVoicesMix?.maxRows ?? DEFAULT_MAX_ROWS;
	const commentsPerVideo = supporterVoicesMix?.commentsPerVideo ?? DEFAULT_COMMENTS_PER_VIDEO;
	const maxItems = itemsPerRow * maxRows;

	const {
		data: { supporterComments, petition },
	} = useSupporterComments();

	const {
		data: { supporterVideos, currentlyPlaying, showCreateVideo },
		actions: { setCurrentlyPlaying, removeVideoFromState },
	} = useSupporterVideos({ supporterVoicesVideos });

	// Reserve one cell for the create-video CTA when it will render, so the CTA shares the last
	// content row instead of pushing that row past the container width. With the default 3×2 this
	// makes the CTA's row hold `itemsPerRow − 1` cards + the CTA (e.g. 2 cards + CTA), which fits
	// without horizontal overflow on desktop (CHANGE-94344). The grid still wraps as a safety net
	// when even that can't fit (e.g. a comments-only feed of 3 fixed-width cards + CTA).
	const itemCap = showCreateVideo ? Math.max(maxItems - 1, 0) : maxItems;

	const feedItems = useMemo(() => {
		// Cap each source before interleaving so we never render more than `itemCap`
		// (itemsPerRow × maxRows, minus one reserved cell when the create-video CTA renders).
		const commentItems: FeedItem[] = supporterComments
			.slice(0, itemCap)
			.map((data) => ({ type: 'comment' as const, data }));
		const videoItems: FeedItem[] = supporterVideos.slice(0, itemCap).map((data) => ({
			type: 'video' as const,
			data: data as SupporterVideo,
		}));

		const items: FeedItem[] = [];
		let commentIdx = 0;
		let videoIdx = 0;

		// Lead each cycle with `commentsPerVideo` messages, then a video, so equal counts
		// alternate M, V, M, V, … (comment-first). The starting type is intentionally fixed here,
		// not an FCM option — the FCM only controls itemsPerRow, maxRows, and the ratio.
		while (items.length < itemCap && (commentIdx < commentItems.length || videoIdx < videoItems.length)) {
			for (let c = 0; c < commentsPerVideo && items.length < itemCap; c += 1) {
				const comment = commentItems.at(commentIdx);
				if (comment) {
					items.push(comment);
					commentIdx += 1;
				}
			}
			if (items.length < itemCap) {
				const video = videoItems.at(videoIdx);
				if (video) {
					items.push(video);
					videoIdx += 1;
				}
			}
		}

		return items;
	}, [supporterComments, supporterVideos, itemCap, commentsPerVideo]);

	return {
		data: {
			feedItems,
			petition,
			currentlyPlaying,
			showFeed: feedItems.length >= MIN_ITEMS_TO_SHOW,
			showCreateVideo,
			itemsPerRow,
		},
		actions: {
			setCurrentlyPlaying,
			removeVideoFromState,
		},
	};
}
